import { useTheme } from "@emotion/react";
import React, { useMemo, useState } from "react";
import { MdEdit } from "react-icons/md";
import { useQueryClient } from "react-query";

import { useClientManager } from "@megaron/react-clients";
import { CustomerDoc } from "@megaron/docs-contracts";
import { TicketStatus } from "@megaron/support-contracts";
import { Uuid } from "@megaron/uuid";
import { Button } from "@dash/form";
import { DocsSelectDialog } from "@dash/select";

import { CrmCustomerCard } from "./CrmCustomerCard";
import { SupportEditTagsDialog } from "./SupportEditTagsDialog";

type Props = {
  ticketId: Uuid;
  subject: string;
  status: TicketStatus;
  priority: "low" | "medium" | "high" | "urgent" | null;
  category: "billing" | "technical" | "sales" | "complaint" | "other" | null;
  reporterEmail: string;
  crmCustomerUuid: Uuid | null;
  tags: string[];
};

function matchCustomerByEmail(items: CustomerDoc[], email: string): CustomerDoc | undefined {
  const lower = email.trim().toLowerCase();
  return items.find((c) => c.email?.trim().toLowerCase() === lower);
}

export const SupportTicketContactCrmCard: React.FC<Props> = ({
  ticketId,
  subject,
  status,
  priority,
  category,
  reporterEmail,
  crmCustomerUuid,
  tags,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);

  const bulkQuery = useClientManager("crm")
    .customerDocBulkQuery()
    .useQuery(
      { customerUuids: crmCustomerUuid ? [crmCustomerUuid] : [] },
      { enabled: !!crmCustomerUuid },
    );

  const searchQuery = useClientManager("docs")
    .searchCustomers()
    .useQuery(
      { searchText: reporterEmail.trim(), limit: 10 },
      { enabled: !crmCustomerUuid && !!reporterEmail.trim() },
    );

  const updateMutation = useClientManager("support")
    .updateTicket()
    .useMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
      },
    });

  const customerByUuid = useMemo(() => {
    if (!crmCustomerUuid) return undefined;
    return bulkQuery.data?.[0] ?? undefined;
  }, [crmCustomerUuid, bulkQuery.data]);

  const customerByEmail = useMemo(() => {
    if (crmCustomerUuid) return undefined;
    return matchCustomerByEmail(searchQuery.data?.items ?? [], reporterEmail);
  }, [crmCustomerUuid, searchQuery.data?.items, reporterEmail]);

  const displayCustomer = customerByUuid ?? customerByEmail;
  const payloadBase = {
    id: ticketId,
    subject,
    status,
    priority,
    category,
    tags,
    crmCustomerUuid,
  };
  const buildUpdatePayload = (nextCrmCustomerUuid: Uuid | null) => ({
    ...payloadBase,
    crmCustomerUuid: nextCrmCustomerUuid,
  });

  const isLoading =
    (crmCustomerUuid ? bulkQuery.isLoading : false) ||
    (!crmCustomerUuid ? searchQuery.isLoading : false);

  if (isLoading) {
    return (
      <div css={{ padding: "0.75rem 1rem", border: `1px solid ${theme.colors.border}`, borderRadius: theme.smallBorderRadius, fontSize: "14px", color: theme.colors.secondaryText }}>
        Szukam w CRM...
      </div>
    );
  }

  if (displayCustomer) {
    const handleDetach = customerByUuid
      ? () => {
          updateMutation.mutate(buildUpdatePayload(null));
        }
      : undefined;

    return (
      <>
        <CrmCustomerCard customer={displayCustomer} onDetach={handleDetach} />
        <div css={{ display: "flex", justifyContent: "space-between" }}>
          <div css={{ display: "flex", gap: "7px", alignItems: "center", color: theme.colors.primary, flexWrap: "wrap" }}>
            {tags.map((tag) => (
              <div
                css={{
                  borderRadius: theme.smallBorderRadius,
                  background: theme.colors.primaryLight,
                  width: "fit-content",
                  alignItems: "center",
                  padding: "5px 10px",
                  color: theme.colors.primary,
                  cursor: "pointer",
                }}
                key={tag}
              >
                {`#${tag}`}
              </div>
            ))}
            <Button icon={<MdEdit />} variant="text" onClick={() => setTagsDialogOpen(true)}>
              {tags.length ? "Edytuj" : "Dodaj Tag"}
            </Button>
          </div>
        </div>
        {tagsDialogOpen && (
          <SupportEditTagsDialog
            onClose={() => setTagsDialogOpen(false)}
            ticketId={ticketId}
            subject={subject}
            status={status}
            priority={priority}
            category={category}
            crmCustomerUuid={crmCustomerUuid}
            ticketTags={tags}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div css={{ padding: "0.75rem 1rem", border: `1px solid ${theme.colors.border}`, borderRadius: theme.smallBorderRadius, fontSize: "14px" }}>
        <span css={{ color: theme.colors.secondaryText }}>Brak w CRM</span>
        <button
          type="button"
          onClick={() => setAssignDialogOpen(true)}
          css={{ display: "block", marginTop: "0.25rem", fontSize: "14px", fontWeight: 600, color: theme.colors.primary, background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", "&:hover": { textDecoration: "underline" } }}
        >
          Przypisz
        </button>
      </div>
      <DocsSelectDialog
        docType={["customer"]}
        variant="single-select"
        label="Przypisz klienta z CRM"
        noTrigger
        isOpen={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onSelect={(option) => {
          if (option && option.docType === "customer" && option.value) {
            setAssignDialogOpen(false);
            updateMutation.mutate(buildUpdatePayload(Uuid(option.value)));
          }
        }}
      />
      <div css={{ display: "flex", justifyContent: "space-between" }}>
        <div css={{ display: "flex", gap: "7px", alignItems: "center", color: theme.colors.primary, flexWrap: "wrap" }}>
          {tags.map((tag) => (
            <div
              css={{
                borderRadius: theme.smallBorderRadius,
                background: theme.colors.primaryLight,
                width: "fit-content",
                alignItems: "center",
                padding: "5px 10px",
                color: theme.colors.primary,
                cursor: "pointer",
              }}
              key={tag}
            >
              {`#${tag}`}
            </div>
          ))}
          <Button icon={<MdEdit />} variant="text" onClick={() => setTagsDialogOpen(true)}>
            {tags.length ? "Edytuj" : "Dodaj Tag"}
          </Button>
        </div>
      </div>
      {tagsDialogOpen && (
        <SupportEditTagsDialog
          onClose={() => setTagsDialogOpen(false)}
          ticketId={ticketId}
          subject={subject}
          status={status}
          priority={priority}
          category={category}
          crmCustomerUuid={crmCustomerUuid}
          ticketTags={tags}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
          }}
        />
      )}
    </>
  );
};
